import { useContext, useEffect, useRef, useState } from 'react';

// Components
import { Button } from '@/components/Button/Button';
import { Context } from '@/components/Store';
import { ActionTypes } from '@/components/Store.d';
// Modules
import { setInnerHTML } from '@/lib/util';

import type { DialogAction, DialogOnClick } from './Dialog.d';

const Dialog = (): React.ReactElement => {
  const [state, dispatch] = useContext(Context);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const dialogRef = useRef<HTMLDivElement>(null);

  const handleClose = () => {
    dispatch({
      type: ActionTypes.hideDialog
    });
  };

  const handleClick = async (callback: DialogOnClick) => {
    // Articificual delay to avoid fast-changing loading indicator for short callback operations
    setTimeout(() => setIsLoading(true), 50);

    await callback();
    setIsLoading(false);
  };

  useEffect(() => {
    const isVisible = !state?.dialog?.isHidden;

    if (!isVisible) return;

    const focusableSelectors = ['button', 'a[href]', 'input', 'textarea', 'select', '[tabindex]:not([tabindex="-1"])'];
    const modal = dialogRef.current;
    const focusableElements = modal?.querySelectorAll<HTMLElement>(focusableSelectors.join(','));

    const firstEl = focusableElements?.[0];
    const lastEl = focusableElements?.[focusableElements.length - 1];

    firstEl?.focus();

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        handleClose();
      }

      if (e.key === 'Tab') {
        if (!firstEl || !lastEl) return;

        if (e.shiftKey) {
          if (document.activeElement === firstEl) {
            e.preventDefault();
            lastEl.focus();
          }
        } else {
          if (document.activeElement === lastEl) {
            e.preventDefault();
            firstEl.focus();
          }
        }
      }
    };

    const handleFocusIn = (e: FocusEvent) => {
      if (modal && !modal.contains(e.target as Node)) {
        e.preventDefault();
        firstEl?.focus();
      }
    };

    document.addEventListener('focusin', handleFocusIn);
    document.addEventListener('keydown', handleKeyDown);

    // eslint-disable-next-line consistent-return
    return () => {
      document.removeEventListener('focusin', handleFocusIn);
      document.removeEventListener('keydown', handleKeyDown);
    };
    // handleClose is stable and intentionally not in deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state?.dialog?.isHidden, dispatch]);

  const isVisible = !state?.dialog?.isHidden;

  return (
    <div
      aria-describedby="dialog-body"
      aria-labelledby="dialog-title"
      aria-modal="true"
      className="dialog dialog-width525"
      hidden={!isVisible}
      ref={dialogRef}
      role="dialog"
    >
      <div className="dialog--surface">
        {state?.dialog?.title ? (
          <h1
            className="dialog--title"
            id="dialog-title"
          >
            {state.dialog.title}
          </h1>
        ) : null}
        {state?.dialog?.body ? (
          <p
            dangerouslySetInnerHTML={setInnerHTML(state.dialog.body)}
            id="dialog-body"
          ></p>
        ) : null}

        {state?.dialog?.type === 'confirm' ? (
          <div className="dialog--actions">
            <Button
              ariaLabel="Schließen"
              className="form--button-secondary form--button-small"
              disabled={isLoading}
              onClick={handleClose}
              type="button"
            >
              Abbrechen
            </Button>
            {state?.dialog?.actions?.map((action: DialogAction, index: number) => (
              <Button
                ariaLabel={action.label || 'OK'}
                className={(action.className ? action.className : 'form--button-primary form--button-small') + (isLoading ? ' loading' : '')}
                disabled={isLoading}
                key={index}
                onClick={() => void handleClick(action.onClick)}
                type="button"
              >
                {action.label || 'OK'}
              </Button>
            ))}
          </div>
        ) : (
          <div className="dialog--actions">
            <Button
              ariaLabel="Schließen"
              className="form--button-secondary form--button-small"
              onClick={handleClose}
              type="button"
            >
              OK
            </Button>
          </div>
        )}
      </div>
      <div className="dialog--backdrop"></div>
    </div>
  );
};

export { Dialog };
